import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, MessageSquarePlus } from "lucide-react";

export default function CoachPlayer() {
  const { playerId } = useParams();
  const t = useT();
  const [dash, setDash] = useState(null);
  const [entries, setEntries] = useState([]);
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [player, setPlayer] = useState(null);

  const load = async () => {
    const d = await api.get(`/dashboard?player_id=${playerId}`); setDash(d.data);
    const e = await api.get(`/entries?player_id=${playerId}`); setEntries(e.data);
    const n = await api.get(`/coach-notes?player_id=${playerId}`); setNotes(n.data);
    const players = await api.get("/coach/players"); setPlayer(players.data.find((p) => p.player.id === playerId));
  };
  useEffect(() => { load(); }, [playerId]);

  const addNote = async () => {
    if (!text.trim()) return;
    try { await api.post("/coach-notes", { player_id: playerId, text }); setText(""); load(); toast.success("Note added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!dash) return <div className="max-w-3xl mx-auto space-y-3">{[1, 2].map((i) => <div key={i} className="skeleton h-32" />)}</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <Link to="/coach" className="text-sm text-slate-500 flex items-center gap-1"><ArrowLeft size={14} /> {t("Back to players")}</Link>
      <div className="ct-card p-6">
        <h1 className="font-head font-bold text-2xl">{player?.player?.name || t("Player")}</h1>
        <p className="text-slate-500 text-sm">{player?.profile?.role_type} · {player?.profile?.batting_hand}</p>
        <div className="grid grid-cols-4 gap-3 mt-4">
          <S l={t("Runs")} v={dash.batting.runs} /><S l={t("Avg")} v={dash.batting.average ?? "-"} /><S l={t("Wkts")} v={dash.bowling.wickets} /><S l={t("Form")} v={dash.form_index ?? "-"} />
        </div>
      </div>

      <div className="ct-card p-5" data-testid="coach-note-composer">
        <div className="flex items-center gap-2 mb-2"><MessageSquarePlus size={16} className="text-brand" /><span className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("Add a note")}</span></div>
        <textarea className="w-full rounded-lg border border-slate-200 p-3 text-sm" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Great tempo against spin — keep working on the pull." data-testid="coach-note-input" />
        <Button className="bg-brand hover:bg-brand-dark mt-2" onClick={addNote} data-testid="coach-note-submit-button">{t("Post note")}</Button>
        <p className="text-xs text-slate-400 mt-2">You can view stats and add notes. Coaches can't edit player stats.</p>
      </div>

      {notes.length > 0 && (
        <div className="ct-card p-5"><h2 className="font-head font-bold text-xl mb-3">{t("Notes")}</h2>
          {notes.map((n) => <div key={n.id} className="border-l-2 border-brand pl-3 py-1 mb-2"><p className="text-sm">{n.text}</p><p className="text-xs text-slate-400">{new Date(n.created_at).toLocaleString()}</p></div>)}
        </div>
      )}

      <div className="ct-card p-5"><h2 className="font-head font-bold text-xl mb-3">{t("Recent entries")}</h2>
        <div className="space-y-2">
          {entries.slice(0, 10).map((e) => (
            <div key={e.id} className="flex justify-between items-center bg-slate-50 rounded-lg p-3 text-sm">
              <span className="font-semibold">{e.opponent || "Session"} <span className="text-slate-400 font-normal">· {e.date}</span></span>
              <span className="font-mono">{e.batting ? `${e.batting.runs}${e.batting.not_out ? "*" : ""}` : ""} {e.bowling ? `${e.bowling.wickets}/${e.bowling.runs_conceded}` : ""}</span>
            </div>
          ))}
          {entries.length === 0 && <p className="text-slate-500 text-sm">No entries yet.</p>}
        </div>
      </div>
    </div>
  );
}
function S({ l, v }) { return <div className="text-center"><div className="stat-num text-2xl tabular">{v}</div><div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">{l}</div></div>; }
